// Household linking for the public "My Schedule" lookup.
//
// A family shares one membership but not one email: Mom registers under
// email A, Dad under email B, and the kids have no email of their own. The
// club's member import file already groups them — every row of a family
// carries the same numeric membership "ID", with "Child" = Yes on the kids:
//
//   ID  | Child | First Name | Last Name | Email
//   001 |       | Neil       | Vogel     | nvogel@dotdashmdp.com
//   001 |       | Teal       | Newland   | teal.newland@gmail.com
//   001 | Yes   | Davis      | Vogel     |
//
// This module turns that file into a `householdId` stamped on each player,
// so the lookup endpoint can answer "who else is in this family?" from an
// email typed by either parent. Kids reach their schedule the same way.

// A minimal RFC-4180 CSV tokenizer: handles quoted fields, escaped ""
// quotes, and commas/newlines inside quotes.
function parseCSVRows(text) {
  const rows = [];
  let row = [], field = "", inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\r") {
      // ignore — handled with the \n
    } else if (c === "\n") {
      row.push(field); rows.push(row); row = []; field = "";
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

const norm = s => String(s ?? "").trim();
const lower = s => norm(s).toLowerCase();

// Household ids are membership numbers that get written both ways — the
// import file says "038", someone typing one into the roster says "38".
// Compare them canonically so the two aren't treated as different families.
// Mirrored by kac_hid() in public/api.php and devserver.js.
export function normalizeHouseholdId(value) {
  const s = String(value ?? "").trim();
  if (!s) return "";
  return /^\d+$/.test(s) ? s.replace(/^0+(?=\d)/, "") : s.toLowerCase();
}

// People are matched to roster players by email when they have one, and by
// name otherwise (kids never do). Collapse case, punctuation and repeated
// spaces so "Klapper-Matos" / "klapper matos" still meet.
export function nameKey(first, last) {
  return `${norm(first)} ${norm(last)}`
    .toLowerCase()
    .replace(/[.'’]/g, "")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// True if this header row looks like the club's member import template
// (rather than the Member Preferences form export, which RosterView also
// accepts). Requires the household ID column plus a name column.
export function isHouseholdCSV(text) {
  const firstLine = String(text ?? "").split(/\r?\n/, 1)[0] || "";
  const cols = (parseCSVRows(firstLine)[0] || []).map(lower);
  return cols.includes("id") &&
    cols.includes("child") &&
    (cols.includes("first name") || cols.includes("last name"));
}

// Parses the member import CSV into households:
//   [{ id: "001", people: [{ firstName, lastName, email, isChild }] }]
// Rows without an ID or a name are skipped; a household with a single person
// is kept (they may gain members later, and it still stamps that player).
export function parseHouseholdCSV(csvText) {
  const rows = parseCSVRows(String(csvText ?? "")).filter(r => r.some(c => norm(c) !== ""));
  if (rows.length < 2) {
    return { error: "That file has no rows to read." };
  }

  const header = rows[0].map(lower);
  const col = name => header.indexOf(name);
  const iId = col("id"), iChild = col("child");
  const iFirst = col("first name"), iLast = col("last name"), iEmail = col("email");
  if (iId === -1 || (iFirst === -1 && iLast === -1)) {
    return { error: "Expected a member import file with ID, First Name and Last Name columns." };
  }

  const byId = new Map();
  let skipped = 0;
  rows.slice(1).forEach(r => {
    const id = norm(r[iId]);
    const firstName = iFirst === -1 ? "" : norm(r[iFirst]);
    const lastName = iLast === -1 ? "" : norm(r[iLast]);
    if (!id || (!firstName && !lastName)) { skipped++; return; }
    if (!byId.has(id)) byId.set(id, { id, people: [] });
    byId.get(id).people.push({
      firstName,
      lastName,
      email: iEmail === -1 ? "" : norm(r[iEmail]),
      isChild: iChild !== -1 && /^(yes|y|true|1)$/i.test(norm(r[iChild])),
    });
  });

  return { households: [...byId.values()], skipped };
}

// Stamps `householdId` onto the roster players named in `households`.
//
// Matching is email-first (exact, case-insensitive) and falls back to the
// person's full name — kids and any adult whose email the club has since
// changed still land in the right family. Returns a NEW players array plus a
// report of what did and didn't match, so the caller can tell the user which
// family members are missing from the roster.
export function applyHouseholds(players, households) {
  const list = Array.isArray(players) ? players : [];
  // An address maps to EVERY player holding it, not just the first. Families
  // routinely register several people under one parent's address, and taking
  // only the first left the others unlinked while consuming the file row that
  // would have matched them by name.
  const byEmail = new Map();
  const byName = new Map();
  list.forEach(p => {
    const email = lower(p.email);
    if (email) {
      if (!byEmail.has(email)) byEmail.set(email, []);
      byEmail.get(email).push(p.id);
    }
    // Player names are stored as one "First Last" string; split on the first
    // space so the key matches the CSV's separate columns.
    const parts = norm(p.name).split(/\s+/);
    const key = nameKey(parts[0] || "", parts.slice(1).join(" ") || norm(p.lastName));
    if (key && !byName.has(key)) byName.set(key, p.id);
  });

  const assigned = new Map();   // playerId -> householdId
  const unmatched = [];         // people in the file with no roster player
  households.forEach(h => {
    h.people.forEach(person => {
      // One row can legitimately claim several players: the person named,
      // plus everyone reachable at the address they gave — that address is
      // this family's, so all its holders belong to this household.
      const targets = new Set(byEmail.get(lower(person.email)) || []);
      const named = byName.get(nameKey(person.firstName, person.lastName));
      if (named !== undefined) targets.add(named);
      if (targets.size === 0) {
        unmatched.push({ ...person, householdId: h.id });
        return;
      }
      // First household to claim a player wins, so a duplicated row in the
      // file can't flip someone into another family.
      targets.forEach(id => { if (!assigned.has(id)) assigned.set(id, h.id); });
    });
  });

  const updated = list.map(p =>
    assigned.has(p.id) ? { ...p, householdId: assigned.get(p.id) } : p
  );

  return {
    players: updated,
    linked: assigned.size,
    households: new Set(assigned.values()).size,
    unmatched,
  };
}

// Everyone whose schedule an email is entitled to see: the players holding
// that email, plus every other player in their household(s). Shared by the
// front end and mirrored by the api.php / devserver lookup endpoints.
export function playersForEmail(players, email) {
  const target = lower(email);
  if (!target) return [];
  const list = Array.isArray(players) ? players : [];
  const direct = list.filter(p => lower(p.email) === target);
  const householdIds = new Set(
    direct.map(p => normalizeHouseholdId(p.householdId)).filter(Boolean)
  );
  if (householdIds.size === 0) return direct;
  const ids = new Set(direct.map(p => p.id));
  return list.filter(
    p => ids.has(p.id) || householdIds.has(normalizeHouseholdId(p.householdId))
  );
}
